import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiX, FiAlertTriangle, FiCheckCircle, FiInfo, FiXCircle } from 'react-icons/fi';
import { getAnnouncements, dismissAnnouncement } from '../api/announcementsApi';
import { isSuperadminPath } from '../utils/routeHelpers';

const baseUrl = process.env.REACT_APP_API_URL;

type Announcement = {
  _id: string;
  type: "info" | "success" | "warning" | "error" | "maintenance";
  title: string;
  message: string;
  icon?: string;
  link?: string;
  linkText?: string;
  priority: number;
  active: boolean;
  pinned?: boolean;
  startDate?: string;
  endDate?: string;
};

export default function AnnouncementsWall() {
  const storageKey = useMemo(() => {
    try {
      const rawClient = localStorage.getItem('client');
      const client = rawClient ? JSON.parse(rawClient) : {};
      const userId = client?._id || 'guest';
      return `dismissedAnnouncements:${userId}`;
    } catch {
      return 'dismissedAnnouncements:guest';
    }
  }, []);

  const apiBase = useMemo(() => {
    const base = baseUrl || window.location.origin;
    return base.replace(/\/$/, '');
  }, []);

  const storageKeyRef = useRef(storageKey);
  const companyRef = useRef<string>('');

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [popup, setPopup] = useState<Announcement | null>(null);
  const [, setTick] = useState<number>(0);
  const [dismissedIds, setDismissedIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(storageKeyRef.current);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [snoozedIds, setSnoozedIds] = useState<string[]>([]);

  useEffect(() => {
    storageKeyRef.current = storageKey;
  }, [storageKey]);

  const dismissedRef = useRef<string[]>(dismissedIds);
  const snoozedRef = useRef<string[]>(snoozedIds);
  useEffect(() => {
    dismissedRef.current = dismissedIds;
  }, [dismissedIds]);

  useEffect(() => {
    snoozedRef.current = snoozedIds;
  }, [snoozedIds]);

  useEffect(() => {
    // CRITICAL: Announcements are company-scoped, NOT available for superadmin
    // Skip fetching announcements on superadmin routes
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    if (isSuperadminPath(pathname)) {
      if (process.env.NODE_ENV === 'development') {
      }
      return;
    }
    
    fetchAnnouncements();
    // CRITICAL FIX: Increase interval from 1000ms to 5000ms to reduce [Violation] 'setInterval' warnings
    // 5 seconds is still frequent enough for UI updates while significantly reducing timer overhead
    const t = setInterval(() => setTick((x) => x + 1), 5000);

    // Listen for new announcements via socket
    const handleNewAnnouncement = (ann: Announcement) => {
      setAnnouncements(prev => {
        const exists = prev.some(a => a._id === ann._id);
        if (exists) return prev;
        return [ann, ...prev].sort((a, b) => b.priority - a.priority);
      });

      setSnoozedIds(prev => prev.filter(id => id !== ann._id));

      if (
        ann.active &&
        !dismissedRef.current.includes(ann._id) &&
        !snoozedRef.current.includes(ann._id)
      ) {
        setPopup(ann);
      }
    };

    const handleUpdatedAnnouncement = (ann: Announcement) => {
      setAnnouncements(prev =>
        prev.map(a => a._id === ann._id ? ann : a).sort((a, b) => b.priority - a.priority)
      );
      setSnoozedIds(prev => prev.filter(id => id !== ann._id));
    };

    const handleDeletedAnnouncement = ({ id }: { id: string }) => {
      setAnnouncements(prev => prev.filter(a => a._id !== id));
    };

    // Listen for socket events via window custom events (SocketBridge emits these)
    const handleAnnouncementNew = (e: CustomEvent) => handleNewAnnouncement(e.detail);
    const handleAnnouncementUpdate = (e: CustomEvent) => handleUpdatedAnnouncement(e.detail);
    const handleAnnouncementDelete = (e: CustomEvent) => handleDeletedAnnouncement(e.detail);

    window.addEventListener('announcement:new', handleAnnouncementNew as EventListener);
    window.addEventListener('announcement:updated', handleAnnouncementUpdate as EventListener);
    window.addEventListener('announcement:deleted', handleAnnouncementDelete as EventListener);

    return () => {
      clearInterval(t);
      window.removeEventListener('announcement:new', handleAnnouncementNew as EventListener);
      window.removeEventListener('announcement:updated', handleAnnouncementUpdate as EventListener);
      window.removeEventListener('announcement:deleted', handleAnnouncementDelete as EventListener);
    };
  }, []);

  useEffect(() => {
    setSnoozedIds(prev => {
      const filtered = prev.filter(id => announcements.some(a => a._id === id));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [announcements]);

  async function fetchAnnouncements() {
    // CRITICAL: Double-check we're not on superadmin route
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
    if (isSuperadminPath(pathname)) {
      if (process.env.NODE_ENV === 'development') {
      }
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      let company = '';
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          company = payload.companyNumber || '';
        } catch {}
      }

      if (company) companyRef.current = company;

      const data = await getAnnouncements(company || undefined);

      const serverDismissed = !Array.isArray(data) && Array.isArray(data?.dismissed)
        ? data.dismissed.map(String)
        : [];
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.announcements)
          ? data.announcements
          : [];

      const suppressed = Array.from(new Set([...dismissedRef.current, ...serverDismissed]));
      localStorage.setItem(storageKeyRef.current, JSON.stringify(suppressed));
      setDismissedIds(suppressed);
      dismissedRef.current = suppressed;

      const sorted = [...list].sort((a: Announcement, b: Announcement) => b.priority - a.priority);
      const active = sorted.filter((a: Announcement) => a.active && !suppressed.includes(a._id));
      setAnnouncements(active);
    } catch (err: any) {
      // Silently handle timeout errors - don't log
      const isTimeout = err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || err?.message?.includes('exceeded');
      if (!isTimeout) {
        // Only log non-timeout errors
        console.error('Failed to fetch announcements:', err);
      }
    }
  }

  useEffect(() => {
    if (popup) {
      if (dismissedIds.includes(popup._id) || snoozedIds.includes(popup._id)) {
        setPopup(null);
        return;
      }
    }
    if (!popup) {
      const candidate = pickTopActive(announcements, dismissedIds, snoozedIds);
      if (candidate) setPopup(candidate);
    }
  }, [announcements, dismissedIds, snoozedIds, popup]);

  function dismiss(id: string) {
    setDismissedIds(prev => {
      if (prev.includes(id)) return prev;
      const updated = [...prev, id];
      localStorage.setItem(storageKeyRef.current, JSON.stringify(updated));
      dismissedRef.current = updated;
      return updated;
    });
    setAnnouncements(prev => prev.filter(a => a._id !== id));
    setSnoozedIds(prev => prev.filter(x => x !== id));
    if (popup?._id === id) setPopup(null);
    void persistDismiss(id);
  }

  const closePopup = () => {
    if (popup?._id) {
      setSnoozedIds(prev => (prev.includes(popup._id) ? prev : [...prev, popup._id]));
    }
    setPopup(null);
  };

  const dismissPopup = () => {
    if (popup?._id) dismiss(popup._id);
    else setPopup(null);
  };

  async function persistDismiss(id: string) {
    try {
      await dismissAnnouncement(id, companyRef.current || undefined);
    } catch (err) {
      console.error('Failed to persist dismissed announcement:', err);
    }
  }

  function formatRemaining(end?: string) {
    if (!end) return null;
    const endTs = new Date(end).getTime();
    const now = Date.now();
    const diff = Math.max(0, endTs - now);
    if (diff <= 0) return '00:00:00';
    const totalSec = Math.floor(diff / 1000);
    const h = String(Math.floor(totalSec / 3600)).padStart(2, '0');
    const m = String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0');
    const s = String(totalSec % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  }

  function renderMessage(text?: string) {
    if (!text) return null;
    return text
      .split(/\r?\n+/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line, idx) => (
        <p key={idx} className="text-sm leading-relaxed text-gray-800 text-right" dir="rtl">
          {line}
        </p>
      ));
  }

  // Schedule end popups for time-bound announcements
  const endTimersRef = useRef<Record<string, number>>({});
  useEffect(() => {
    Object.values(endTimersRef.current).forEach((t) => clearTimeout(t));
    endTimersRef.current = {};

    announcements.forEach((a) => {
      if (a.pinned || !a.endDate) return;
      if (dismissedRef.current.includes(a._id)) return;
      if (snoozedRef.current.includes(a._id)) return;
      const endTs = new Date(a.endDate).getTime();
      const delay = Math.max(0, endTs - Date.now());
      endTimersRef.current[a._id] = window.setTimeout(() => {
        if (dismissedRef.current.includes(a._id)) return;
        if (snoozedRef.current.includes(a._id)) return;
        setPopup({ ...a, message: `${a.message}` });
        setAnnouncements((prev) => prev.filter((x) => x._id !== a._id));
      }, delay);
    });

    return () => {
      Object.values(endTimersRef.current).forEach((t) => clearTimeout(t));
      endTimersRef.current = {};
    };
  }, [announcements, dismissedIds, snoozedIds]);

  function pickTopActive(list: Announcement[], dismissed: string[], snoozed: string[]) {
    const blocked = new Set([...(dismissed || []), ...(snoozed || [])]);
    const candidates = (list || []).filter((a) => a.active && !blocked.has(a._id));
    if (candidates.length === 0) return null;
    return [...candidates].sort((a, b) => b.priority - a.priority)[0];
  }

  return (
    <>
      {/* Popup modal for newly created announcements */}
      {popup && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={closePopup} />
          <div className="relative z-[1001] w-[92vw] max-w-lg rounded-2xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.25)] ring-1 ring-black/10">
            <div className="flex items-start gap-3 p-6">
              {popup.type === 'maintenance' && <FiAlertTriangle className="w-7 h-7 text-red-600 flex-shrink-0" />}
              {popup.type === 'warning' && <FiAlertTriangle className="w-7 h-7 text-yellow-600 flex-shrink-0" />}
              {popup.type === 'error' && <FiXCircle className="w-7 h-7 text-red-600 flex-shrink-0" />}
              {popup.type === 'success' && <FiCheckCircle className="w-7 h-7 text-green-600 flex-shrink-0" />}
              {popup.type === 'info' && <FiInfo className="w-7 h-7 text-blue-600 flex-shrink-0" />}

              <div className="flex-1" dir="rtl">
                <h3 className="mb-1 text-lg font-bold text-gray-900 text-right">{popup.title}</h3>
                <div className="space-y-1">{renderMessage(popup.message)}</div>
                {popup.link && popup.linkText && (
                  <a href={popup.link} className="mt-3 inline-block text-sm font-semibold text-blue-600 underline">
                    {popup.linkText} →
                  </a>
                )}
                {!popup.pinned && popup.endDate && (
                  <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-gray-100 px-2 py-1 text-xs font-mono text-gray-700">
                    נותר: {formatRemaining(popup.endDate)}
                  </div>
                )}
              </div>

              <button onClick={closePopup} className="p-1 text-gray-400 transition hover:text-gray-600" aria-label="סגור">
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="flex items-center justify-end gap-2 border-t px-6 py-3">
              <button onClick={closePopup} className="rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-200">
                סגור
              </button>
              <button onClick={dismissPopup} className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700">
                אל תציג שוב
              </button>
            </div>
            {/* Accent ring */}
            <div className="pointer-events-none absolute -inset-2 -z-10 animate-pulse rounded-[24px] bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-pink-600/10" />
          </div>
        </div>
      )}

      {/* Inline banner stack (existing behavior) */}
      {announcements.length > 0 && (
        <div className="w-full space-y-3 p-4" dir="rtl">
          {announcements.map((ann) => (
            <div
              key={ann._id}
              className={`
                rounded-xl border-l-4 p-4 shadow-lg
                ${ann.type === 'maintenance' ? 'bg-red-50 border-red-500' :
                  ann.type === 'warning' ? 'bg-yellow-50 border-yellow-500' :
                  ann.type === 'error' ? 'bg-red-50 border-red-500' :
                  ann.type === 'success' ? 'bg-green-50 border-green-500' :
                  'bg-blue-50 border-blue-500'}
              `}
            >
              <div className="flex items-start gap-3">
                {ann.type === 'maintenance' && <FiAlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />}
                {ann.type === 'warning' && <FiAlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0" />}
                {ann.type === 'error' && <FiXCircle className="w-6 h-6 text-red-600 flex-shrink-0" />}
                {ann.type === 'success' && <FiCheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />}
                {ann.type === 'info' && <FiInfo className="w-6 h-6 text-blue-600 flex-shrink-0" />}

                <div className="flex-1" dir="rtl">
                  <h3 className="font-bold text-lg text-gray-900 mb-1 text-right">{ann.title}</h3>
                  <div className="space-y-1">{renderMessage(ann.message)}</div>
                  {ann.link && ann.linkText && (
                    <a
                      href={ann.link}
                      className="inline-block mt-2 text-sm font-medium text-blue-600 hover:text-blue-800 underline"
                    >
                      {ann.linkText} →
                    </a>
                  )}
                  {!ann.pinned && ann.endDate && (
                    <div className="mt-1 text-[11px] font-mono text-gray-500">
                      נותר: {formatRemaining(ann.endDate)}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => dismiss(ann._id)}
                  className="text-gray-400 hover:text-gray-600 transition p-1 rounded"
                  aria-label="סגור"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

